/**
 * @fileOverview Scoreboard (/cricket/scores) page parser functions
 */

import selectn from 'selectn'
import { common } from '../../common'
import moment from 'moment-timezone'
import qs from 'query-string'
import helpers from '../gamepackage/helpers'
import { getYearDropDown, isCurrentLeague } from '../../../../utils/common'
import dateFormat       from '../../../../utils/dateformat'
import eventSummary 	  from '../../rugby/eventSummary'
import translate      	from '../../../../utils/translate'

export const scoreboard = {

  setLocalTime: function (leagues = []) {
    const tzOptions = {
      timeZone : moment.tz.guess(),
      timeFormat : 'hh:mm a z'
    }

    leagues.map((league) => {
      const events = league.events || []
      events.map((event) => {
        if(event.gameState === 'pregame') {
          const displayTime = dateFormat.convertDateTime(event.date, tzOptions)
          event.statusText = displayTime
        }

      })
    })
  },

  /**
   * Process data received from the APIs
   * @param  {Object} res       results from API
   * @param  {Object} edition   edition config
   * @param  {Object} params    params
   * @param  {Object} location  Router location
   * @param  {Object} appProps  app Props
   * @return {Object}           leagues and date
   */
  processData: function ( res, edition, params, location, appProps ) {

    // Default return types
    const query = location.query || qs.parse(location.search)
    const device = appProps.device || {}
    const adblock = selectn('_adblock', query) === 'true' ? true : false
    let defaultLeagues = []
    let defaultDate = new Date()
    let title = ''
    let heading = ''
    let defaultActiveDates = []
    let showWeek = false
    let multipleLeagues = true
    let gamesPresent = true
    let seriesId = ''
    let scoreboardType = 'default'
    let currYear = new Date().getFullYear()
    let firstSeries = ''
    // consutruct year array from current calender year to last 6 year
    let dateFilter = getYearDropDown()
    let outbrainSettings = common.getOutbrainSettings(edition, params.pageType, device, adblock) || {}

    // if year is passed as query then current year will fetch from query string else current calender year
    if(typeof query.date !== 'undefined') {
      currYear = query.date
    }

    // Extract sport from params
    let sport
    if (params && Object.keys(params).length > 0) {
      sport = 'sport' in params ? params.sport : 'cricket'
    }

    // Default scoreboard data
    let defaultData = {
      leagues : defaultLeagues,
      date : defaultDate,
      title: translate( edition, ('dropdown.leagues.default.' + sport) ) || '',
      heading: heading,
      activeDates: defaultActiveDates,
      showWeek:showWeek
    }

    // Return default data if res evaluates to a falsy value
    if (!res || res.hasOwnProperty('data') === false) {
      return defaultData
    }

    

    // Return default data if scores are undefined or not an array
    let allScores
    if(selectn('data.scores', res)) {

      allScores = selectn('data.scores', res)

      if(typeof params.team !== 'undefined') {
        multipleLeagues = false
        title = heading = selectn('data.scores[0].leagues[0].name', res) || ''
        scoreboardType = 'team'

        if(typeof query.series !== 'undefined') {
          seriesId = query.series
        } else {
          firstSeries = selectn('data.scores[0].leagues[0].id', res) || ''
          if(firstSeries) {
            seriesId = scoreboard.getCurrentLeague(allScores, firstSeries) || ''
          }
        }
      } else {
        multipleLeagues = true

        // use translation tool to get correct key
        let defaultKey = 'dropdown.leagues.default.' + sport
        title = translate( edition, defaultKey )
        if ( title === defaultKey ) {
          title = translate( edition, 'dropdown.leagues.default.sport' )
        }

        //title = 'All Series'
        heading = 'Cricket Scores'
        if( sport === 'rugby' ) {
          heading = 'Rugby Scores'
          //title = translate(edition, 'dropdown.leagues.default.rugby')
        } else if ( sport === 'rugby-league' ) {
          heading = 'Rugby League Scores'
          //title = translate(edition, 'dropdown.leagues.default.rugby')
        }

      }
    }else if(selectn('data.leagues', res)) {
      multipleLeagues = false
      allScores = [ res.data ]
      title = heading = selectn('data.leagues[0].name', res) || ''
    }
    if ( typeof allScores === 'undefined' || Array.isArray(allScores) === false ) {
      return defaultData
    }

    //console.info('params, res', params, res)

    // Process date of returned games from first game's date
    let date = selectn('data.scores[0].events[0].date', res) || selectn('data.events[0].date', res)
    // use default value if date was not found
    date = typeof date !== 'undefined' ? date : defaultDate

    date = scoreboard.getDate(date, params, query)

    const polling = scoreboard.shouldPoll(date)

    // By default, set fastcast to false
    let fastCastMeta = {
      enabled: false
    }

    /**
     * Fastcast should be enabled only for scores page
     *
     * Fastcast should not be enabled for team & series bases scores pages
     * since we do not have dedicated for those.
     *
     * Also, it should not be enabled for any date other than today's date
     */
    if (params.hasOwnProperty('sport') && params.sport === 'cricket') {
      // Do not enable for team & series scores pages
      if (params.hasOwnProperty('team') === false && params.hasOwnProperty('league') === false) {
        fastCastMeta = {
          enabled: polling.enabled,
          topic : 'scoreboard-cricket'
        }
        // Check if date is passed via query string
        if (query && query.hasOwnProperty('date')) {
          const curDate = moment.utc().format('YYYYMMDD')
          // If queried date is different than current date, disable fastcast
          if (query.date !== curDate) {
            fastCastMeta.enabled = false
            fastCastMeta.topic = undefined
          }
        }
      }
    }

    //console.info('scoreboard parser', fastCastMeta)

    // Iterate over the scores and extract league information

    let leagues = []

    for (var i = 0; i < allScores.length; i++) {
      const data = allScores[i]
      /**
       * data object has 2 properties.
       * extract league information from leagues.
       * extract matches information from events.
       */
      const league = selectn('leagues[0]', data)
      if (typeof league !== 'undefined' && Object.keys(league).length > 0) {
        // set default matches for this league
        let standings = scoreboard.parseStanding(data.standings) || {}
        let matches = []

        if(scoreboardType === 'team') {
          if(seriesId === league.id) {
            title = heading = league.name || ''
            matches = scoreboard.parseEvents( params.sport, data.events, standings, edition ) || []
          }
        } else {
          matches = scoreboard.parseEvents( params.sport, data.events, standings, edition ) || []
        }

        let name = league.name

        if(scoreboardType === 'team' && league.abbreviation) {
          name = league.abbreviation
        }

        // for old leagues where slug is missing, construt league from abbreviation/name
        let slug = league.slug || ''
        if (league.slug === '') {
          slug = name.toLowerCase().replace(/\s/g, '-').replace(/\//g, '-')
        }

        leagues.push({
          name: name,
          text: name,
          abbreviation: league.abbreviation,
          calendar: league.calendar,
          slug: slug,
          id: league.id,
          events : matches,
          sport: sport,
          seriesHome: common.extractUrl(league, 'Series Home', 'desktop')
        })

        if(!matches.length && scoreboardType !== 'team') {
          gamesPresent = false
        }

      }
    }

    if(!leagues.length) {
      gamesPresent = false
    }

    const emptyString = gamesPresent ? '' : 'No games on this date'

    const activeDates = scoreboard.getActiveDates(leagues)
    //console.info('processData output', { leagues, date, activeDates, title })
    const meta = scoreboard.getMetaData( location, sport )

    const analytics = scoreboard.getAnalytics(meta)
//console.log(date)
    return {
      meta,
      analytics,
      leagues,
      date,
      ISOdate : date.format(),
      activeDates,
      title,
      polling,
      fastCastMeta,
      heading,
      showWeek,
      emptyString,
      multipleLeagues,
      scoreboardType,
      currYear,
      dateFilter,
      outbrainSettings,
      adblock
    }
  },

  getAnalytics: function (meta) {
    return {
      chartbeat: {
        title: meta.title
      },
      omniture: {}
    }
  },

  getMetaData: function ( location, sport ) {
    // TODO : check for 'All Series' and restrict from inserting
    const ucSport = sport.charAt(0).toUpperCase() + sport.slice(1)
    return {
      title: 'Live '+ucSport+' Scores & Fixtures - ESPN',
      description: 'Get get live '+sport+' scores and performance stats for tours, leagues, and championship matches on ESPN.',
      keywords: 'live '+sport+' scores, '+sport+' stats, '+sport+' fixtures',
      website: 'article',
      canonical: location.pathname
    }
  },

  /**
   * Is either of home or away team link is present
   * @param  {Object}  teams formatted teams object - from scoreboard.getTeams
   * @return {Boolean}       true if either of team link is present else false
   */
  isTeamLinkPresent: function ( teams = {}) {
    let homeTeamLink = selectn('home.links[0].href',teams)
    let awayTeamLink = selectn('away.links[0].href',teams)
    //console.info(homeTeamLink, awayTeamLink)
    // Return true only if either of home/away team link is found
    return homeTeamLink || awayTeamLink ? true : false
  },

  /**
   * Check if either of weather or venue information present
   * @param  {Object}  ev event details
   * @return {Boolean}    true if weather/venue info is present else false
   */
  isWeatherVenuePresent: function (ev) {
    const competition = selectn('competitions[0]', ev) || {}
    return common.isVenuePresent(competition) || common.isWeatherInfoPresent(ev)
  },

  /**
   * Check if headToHeadSummary information present
   * @param  {Object}  headToHead headToHeadSummary details
   * @return {Boolean} false if empty else true
   */
  isHeadToHeadPresent: function (headToHead = {}) {
    const headSummaryLength = headToHead.length
    // check if the teams have never player before
    let playedBefore = selectn('[0].totalCompetitions', headToHead) != 0 ? true : false
    return headSummaryLength && playedBefore ? true : false
  },

  /**
   * Check if article information present
   * @param  {Array}  article details
   * @return {Boolean} false if empty else true
   */
  isArticlePresent: function (article = {}) {
    const articleLength = article.length
    return articleLength ? true : false
  },

  /**
   * Check if videos information present
   * @param  {Array}  videos details
   * @param  {Array}  highlights details
   * @param  {String}  gameState details
   * @return {Boolean} false if empty else true
   */
  isVideoPresent: function (videos = [], highlights = [], gameState = '') {
    let videosLength = 0
    if (gameState === 'pregame') {
      videosLength = videos.length
    } else if (gameState === 'live') {
      if (videos.length) {
        videosLength = videos.length
      } else {
        videosLength = highlights.length
      }
    } else if (gameState === 'final') {
      videosLength = highlights.length
    }

    return videosLength > 0 ? true : false
  },

  isStanding: function (standings = {}) {
    let home = selectn('home',standings)
    let away = selectn('away',standings)
    // Return true only if both of home/away team detail is found
    return home && away ? true : false
  },
  /**
   * Check if playerCardsData information present
   * @param  {Object}  playerCardsData details
   * @return {Boolean} false if empty else true
   */
  isPlayerCardPresent: function (playerCardsData = {}) {
    let batter = selectn('batsmen',playerCardsData)
    let bowler = selectn('bowler',playerCardsData)
    //console.info(batter, bowler)
    // Return true only if either of batter/bowler team link is found
    return batter || bowler ? true : false
  },

  /**
   * Check if topperformersData information present
   * @param  {Object}  competition details
   * @return {Boolean} false if empty else true
   */
  isTopperformerPresent: function (competition = {}) {
    const topPerformers = selectn('topperformers', competition) || []
    let isPresent = false
    if(typeof topPerformers !== 'undefined' && topPerformers.length) {
      isPresent = true
    }
    return isPresent
  },

  /**
   * Check if playerstowatchData information present
   * @param  {Object}  playerstowatchData details
   * @return {Boolean} false if empty else true
   */
  isPlayertoWatchPresent:  function (playerstowatchData = []) {
    let isPlayerWatch = false
    if(playerstowatchData.length > 0) {
      (playerstowatchData[0] || []).map((player={}) => {
        if(typeof player.leaders !== 'undefined') {
          isPlayerWatch = true
        }
      })
    }
    return isPlayerWatch
  },

  /**
   * Get details for pre game state
   * @param  {Object} options.flags Flags for a match
   * @return {Object}               which detail should be shown and array of details for the match
   *
   * In order of priority:
   * match preview, match point video, Head to Head, points table, players to
   * watch,team links, weather
   */
  getPregameDetail: function ( { flags = {} }) {
    // default pre match detail type is team information
    let detailsAll = []
    if(flags.articleData) {
      detailsAll.push('article')
    }
    if(flags.videoNode) {
      detailsAll.push('video')
    }
    if(flags.headNode) {
      detailsAll.push('headToHead')
    }
    if(flags.standingsNode) {
      detailsAll.push('pointsTable')
    }
    if(flags.playerWatchNode) {
      detailsAll.push('playersToWatch')
    }
    if (flags.teamlink) {
      detailsAll.push('teamInformation')
    }
    return {
      details : detailsAll[0],
      detailsAll
    }
  },


  /**
   * Get details for in game state
   * @param  {Object} options.flags Flags for a match
   * @return {Object}               which detail should be shown and array of details for the match
   *
   * In order of priority:
   * match point video, match highlights latest clip, batsman/bowler card,
   * match report, points table, players to watch, team links, weather
   */
  getInGameDetail: function ( { flags = {} }) {
    // default pre match detail type is team information
    let detailsAll = []
    //console.info('flags: ', flags)
    if(flags.videoNode) {
      detailsAll.push('video')
    }
    if(flags.articleData) {
      detailsAll.push('article')
    }
    if(flags.playerNode) {
      detailsAll.push('playersTable')
    }
    if(flags.standingsNode) {
      detailsAll.push('pointsTable')
    }
    if (flags.teamlink) {
      detailsAll.push('teamInformation')
    }
    return {
      details : detailsAll[0],
      detailsAll
    }
  },


  /**
   * Get details for post game state
   * @param  {Object} options.flags Flags for a match
   * @return {Object}               which detail should be shown and array of details for the match
   *
   * In order of priority:
   * match highlights, top performer, match report, points table, team links
   */
  getPostGameDetail: function ( { flags = {} }) {
    // default pre match detail type
    let detailsAll = []
    if(flags.videoNode) {
      detailsAll.push('video')
    }
    if(flags.topperformerNode) {
      detailsAll.push('topPerformers')
    }
    if(flags.articleData) {
      detailsAll.push('article')
    }
    if(flags.standingsNode) {
      detailsAll.push('pointsTable')
    }
    if (flags.teamlink) {
      detailsAll.push('teamInformation')
    }
    return {
      details : detailsAll[0],
      detailsAll
    }
  },


  /**
   * Determinw which game details should be shown
   * @param  {String} options.gameState pregame/live/final
   * @param  {Object} options.flags     flags for the match
   * @return {Object}                   detail that needs to be shown
   * @example
   *
   * detailType = {
   *   details : 'video',
   *   detailsAll : ['video', 'topPerformers']
   * }
   *
   */
  getDetailType: function ( { gameState, flags }) {
    // default match details area
    let detailType = {
      details : ''
    }
    // Match details with least priority is weather
    if (flags.weatherVenue) {
      detailType = { details : 'weather' }
    }detailType
    if (gameState === 'pregame') {
      detailType = scoreboard.getPregameDetail( { flags })
    }
    else if (gameState === 'live') {
      detailType = scoreboard.getInGameDetail( { flags })
    }
    else if (gameState === 'final') {
      detailType = scoreboard.getPostGameDetail( { flags })
    }
    return detailType
  },

  parseStanding: function (standings = []) {
    let teamStanding = {}
    // Return empty array if there are no standings
    if(standings.length === 0) {
      return teamStanding
    }
    // Iterate over each standings and extract team information
    standings.map((stnd = {}) => {
      const stats = stnd.stats || []
      const team = stnd.team || {}
      teamStanding[team.id] = {
        id: team.id,
        name: team.name,
        abbr: team.abbreviation,
        logo: common.getTeamLogo(team),
        href: common.getTeamUrl(team),
        points: selectn('[1].value', stats) || 0,
        position: selectn('[0].value', stats) || 0
      }
    })
    //console.info('teamStanding: ', teamStanding)
    return teamStanding
  },

  /**
   * Parse events/games for cricket
   * @param  {String} sport     The current sport
   * @param  {Array}  events    list of sports matches
   * @param  {Object} standings standings
   * @param  {Object} edition   edition
   * @return {Array}            program friendly parsed list of matches.Each Match
   *                            object will have keys {details, gameState, links,
   *                            statusText, teams, weather, winnerClass}
   */
  parseEvents: function ( sport = '', events = [], standings = {}, edition ) {
    // Return empty array if there are no events
    if (events.length === 0) {
      return events
    }
    // Iterate over each event/game and extract information
    let parsedEvents = events.map((ev = {}) => {
      const gameId = ev['id']
      const date = moment(ev['date']).format('MMMM Do, YYYY')
      const competition = selectn('competitions[0]', ev) || {}
      const state = selectn('status.type.state', ev)
      const competitors = competition.competitors || []
      const details = competition.details || []
      const headToHeadData = selectn('headToHeadSummary', ev) || []
      const eventclass = ev.class || {}
      //const status = ev.status || {}
      const links = selectn('links', ev) || []
      const playerCardsData = competition.playerCards || {}
      const playerstowatchData = competition.playerstowatch || []
      const topperformers = competition.topperformers || []
      const videos = competition.videos || []
      const highlights = competition.highlights || []
      const article = competition.article || {}
      const gameState = common.getGameState(state)
      const teams = scoreboard.getTeams(competitors, gameState)
      const headSummary = scoreboard.getHeadSummary(headToHeadData, competitors, eventclass)
      const playerData = scoreboard.getPlayerData(playerCardsData, competitors)
      const playerWatchData = scoreboard.getPlayerToWatchData(playerstowatchData, competitors)
      const topPerformersData = scoreboard.getTopPerformer(topperformers, competitors)
      const videoData = scoreboard.getVideo(videos, highlights, gameState)
      const standingsData = scoreboard.getScoresStandings(standings, competitors)
      const articleData = scoreboard.getArticle(article)
      const info = competition.shortDescription
      // set frequently used flags

      const eventSummaryData = eventSummary.getSummaryFromDetails( sport, edition, competitors, details )
      const eventClock = selectn('status.displayClock', competition) || '0\''

      let flags = {
        weatherVenue : scoreboard.isWeatherVenuePresent(competition),
        teamlink : scoreboard.isTeamLinkPresent(teams),
        headNode : scoreboard.isHeadToHeadPresent(headToHeadData),
        playerNode : scoreboard.isPlayerCardPresent(playerCardsData),
        playerWatchNode : scoreboard.isPlayertoWatchPresent(playerstowatchData),
        topperformerNode : scoreboard.isTopperformerPresent(competition),
        videoNode : scoreboard.isVideoPresent(videos, highlights, gameState),
        standingsNode : scoreboard.isStanding(standingsData),
        articleNode : scoreboard.isArticlePresent(article)
      }
      //console.info('flags: ', flags)
      let detailType = scoreboard.getDetailType( { gameState, teams, flags } )
      //console.info('detailType', detailType)
      const parsedEvent = {
        ...detailType,
        gameId: gameId,
        gameDate: date,
        date: ev['date'],
        teams: teams,
        gameState: gameState,
        statusText: scoreboard.getCricketGameStatus( sport, competition, edition.timeZoneBucket ),
        winnerClass: common.getWinnerClass(competitors),
        weather: {
          venue: common.getVenueName(competition),
          temperature: common.getTemperature(competition),
          conditions: common.getClassNameForSkyCode(competition)
        },
        links: common.getGameLinks(links, sport, edition.userGeo),
        gameUrl: common.getUrlByRel({ links }, 'match'),
        note: scoreboard.getGameNote(ev),
        teamInformation: teams,
        headToHead: headSummary,
        playersTable: playerData,
        playersToWatch : playerWatchData,
        topPerformers: topPerformersData,
        video: videoData,
        pointsTable: standingsData,
        article: articleData,
        info : info,
        eventSummary : eventSummaryData,
        eventClock
      }
      return parsedEvent
    })

    return parsedEvents
  },


  /**
   * Wrapper function for passing cricket specific options to getGameStatusText
   * @param  {[type]} sport           Sport
   * @param  {[type]} competition     competition object describing game
   * @param  {String} timeZoneBucket  timeZoneBucket
   * @return {[type]}                 Output of getGameStatusText
   */
  getCricketGameStatus: function ( sport, competition, timeZoneBucket ) {
    let tzOptions = {
      // Set time zone to IST
      // TODO: use time zones based on editions
      timeZone : process.browser && moment.tz.guess() || timeZoneBucket,
      timeFormat : 'hh:mm a z'
    }
    //console.info('tzOptions', tzOptions)
    return common.getGameStatusText( sport, competition, tzOptions )
  },


  /**
   * Get game note
   * @param  {Object} ev event/game object
   * @return {String}    game note if found else empty string
   */
  getGameNote: function (ev = {}) {
    let defaultNote = ''
    let session = selectn('status.session', ev) || ''
    if(session) {
      session = session + ': '
    }
    let gameNote = session + selectn('status.summary', ev)
    // Return game note if found else return default game note
    return typeof gameNote !== 'undefined' ? gameNote : defaultNote
  },


  /**
   * Get home and away teams details
   * @param  {Array}  competitors competitors array from feed
   * @param  {String} gameState pregame/live/final
   * @return {Object}             teams object with {home, away}
   */
  getTeams: function (competitors=[], gameState) {
    let teams = {}
    let teamsOrder = []
    if(competitors.length > 1 && (selectn('[0].homeAway', competitors) === selectn('[1].homeAway', competitors))) {
      competitors[0].homeAway = 'home'
      competitors[1].homeAway = 'away'
    }
    competitors.map((competitor) => {
      // default to empty object if not available
      const team = competitor.team || {}
      let teamScore = scoreboard.getTeamScores(competitor)
      // Get breakup of runs and overs/target
      let [ runDetails, overDetails ] = common.splitScore(teamScore)
      // Is this team batting now?
      let isTeamBattingNow = helpers.isTeamBattingNow(competitor, gameState)
      // If team is batting in current innings, suffix * as per cricket convention
      let scoreMarkup = common.getFullScoreMarkup(teamScore, 'sm', 'div', true, isTeamBattingNow, false)
      // Populate teams object with home and away data
      teams[competitor.homeAway] = {
        name: team.name,
        abbr: team.abbreviation,
        logo: common.getTeamLogo(team),
        href: common.getTeamUrl(team),
        score: teamScore,
        runDetails: runDetails,
        overDetails: overDetails,
        scoreMarkup: scoreMarkup,
        links: scoreboard.getTeamLinks(team),
        winner: competitor.winner || false,
        record: 'Form: ' + competitor.form || '-',
        order: competitor.order
      }
    })
    let homeTeamOrder = selectn('home.order',teams) || 0
    let awayTeamOrder = selectn('away.order', teams) || 0
    if(homeTeamOrder > 0 && awayTeamOrder > 0) {
      teamsOrder = homeTeamOrder == 1 ? [ 'home','away' ] : [ 'away', 'home' ]
    }
    else {
      Object.keys(teams).map((key)=> {
        teamsOrder.push(key)
      })
    }
    return { ...teams, teamsOrder }
  },

  /**
   * Get article details
   * @param  {Array}  article array from feed
   * @return {Object} article details
   */
  getArticle: function (article = {}) {
    let articleData = {}
    const articleLength = article.length
    if(articleLength) {
      articleData = {
        id: article.id ,
        headline:article.headline ,
        description: article.headline, //description breaking ui so updated with headline
        thumbnail: scoreboard.getThumbnail(article.images),
        showCaption: true
      }
    }
    return articleData
  },

  /**
   * Get thumbnail details
   * @param  {Array}  image array from feed
   * @return {Object} thumbnail source
   */
  getThumbnail: function (image = []) {
    return selectn('[0].url', image) || ''
  },

  /**
   * Get videos details
   * @param  {Array}  videos array from feed
   * @param  {Array}  highlights array from feed
   * @param  {String} gameState details
   * @return {Object} video details
   */
  getVideo: function (videos = [], highlights = [], gameState = '') {
    let matchVideo = []
    let video = []
    if (gameState === 'pregame') {
      matchVideo = videos
    } else if (gameState === 'live') {
      if (videos.length) {
        matchVideo = videos
      } else {
        matchVideo = highlights
      }
    } else if (gameState === 'final') {
      matchVideo = highlights
    }
    const videosLength = matchVideo.length
    if(typeof videosLength !== 'undefined' && videosLength > 0) {
      matchVideo.map((videoData = {}) => {
        video.push({
          id: videoData.id ,
          headline:videoData.headline ,
          description: videoData.headline, //description breaking ui so updated with headline
          source: videoData.source,
          thumbnail: videoData.thumbnail,
          duration: videoData.duration,
          showCaption: true,
          geoRestrictions: videoData.geoRestrictions
        })
      })
    }
    //console.info('video: ', video)
    return video
  },

  /**
   * filter and order stats by names
   * @param  {Object}  stats stats node
   * @param  {Object} names stats to filter, ex:{ name: '', index: 0 }
   * @return {Array}       [description]
   */

  filterStats: function (stats = {}, names = {}) {
    let filteredStat = {}
    let extractedStats = []
    Object.keys(stats).map((stat) => {
      filteredStat = names[stat]
      if(filteredStat) {
        const position = typeof filteredStat.index === 'number' ? filteredStat.index : extractedStats.length
        extractedStats[ position ] = {
          name: filteredStat.text,
          value: stats[stat]
        }
      }
    })
    return extractedStats
  },

  /**
   * Get topperformers batsmen and bowler details
   * @param  {Array}  competitors competitors array from feed
   * @return {Array} topperformers array with {batsmen, bowler's runs and wicket details}
   */

  getTopPerformer: function (topperformers=[], competitors=[]) {
    let player = {}
    let teams = {}
    let statMap = {}

    statMap['runs'] = {
      runs: { text: 'R', index: 0 },
      balls: { text: 'B', index: 1 },
      fours: { text: '4S', index: 2 },
      sixes: { text: '6S', index: 3 }
    }
    statMap['wickets'] = {
      overs: { text: 'O', index: 0 },
      maidens: { text: 'M', index: 1 },
      runs: { text: 'R', index: 2 },
      wickets: { text: 'W', index: 3 },
      economyRate: { text: 'Eco', index: 4 }
    }

    competitors.map((competitor = {}) => {
      // Populate teams object with team id and abbreviation data
      teams[competitor.id] = selectn('team.abbreviation', competitor) || ''
    })

    let runWicketMap = {
      wickets: 'bowler',
      runs: 'batsman'
    }

    topperformers.map((topperformer) => {
      let leaders = selectn('leaders[0]', topperformer) || {}
      const teamId = selectn('team.id', leaders)
      const athlete = leaders.athlete || {}
      const athleteName = topperformer.name
      let headshot = athlete.headshot || ''
      headshot = headshot ? common.resizeCombinerLogo(headshot, { w:108, h:108, transparent:true, scale:'crop'  } ) : ''
      player[runWicketMap[athleteName]] = {
        name: athlete.displayName,
        href: selectn('links[0].href', athlete),
        mugshot: headshot,
        teamAbbr: teams[teamId] || '',
        stats: scoreboard.filterStats(leaders[athleteName], statMap[athleteName])
      }
    })
    return player
  },

  /**
   * Get PlayerToWatch batsmen and bowler details
   * @param  {Array}  playerstowatchData players to watch
   * @param  {Array}  competitors competitors array from feed
   * @return {Object} playerstowatchData object with {batsmen, bowler's runs and wicket details}
   */
  getPlayerToWatchData: function (playerstowatchData=[], competitors=[]) {
    let teams = {}
    let players = {}
    let batterDetails = []
    let bowlerDetails = []

    competitors.map((competitor) => {
      // Populate teams object with team id and abbreviation data
      teams[competitor.id] = competitor.team.abbreviation || ''
    })
    // construct batter and bowler array for the players table
    playerstowatchData.map((player) => {
      (player || []).map((ply={}) => {
        // Get athlete name, team id, abbreviation
        let athleteName = selectn('leaders[0].athlete.displayName', ply) || ''
        let athleteTeamId = selectn('leaders[0].team.id', ply)
        let teamAbbr = ''
        if (teams.hasOwnProperty(athleteTeamId)) {
          teamAbbr = teams[athleteTeamId]
        }

        if(ply.name === 'runs' && typeof ply.leaders !== 'undefined') {
          let statbatdetail = []
          let runs = selectn('leaders[0].runs.runs', ply) || ''
          statbatdetail.push(
            {
              name: 'matches',
              value: selectn('leaders[0].matches', ply) || ''
            }, {
              name: 'runs',
              value: runs
            }
          )

          batterDetails.push({
            name : athleteName,
            teamAbbr: teamAbbr,
            stats: statbatdetail,
            runs: runs
          })
        } else if(ply.name === 'wickets' && typeof ply.leaders !== 'undefined') {
          let statbowldetail = []
          let wickets = selectn('leaders[0].wickets.wickets', ply)
          statbowldetail.push(
            {
              name: 'matches',
              value: selectn('leaders[0].matches', ply)
            }, {
              name: 'wickets',
              value: wickets
            }
          )

          bowlerDetails.push({
            name : athleteName,
            teamAbbr: teamAbbr,
            stats: statbowldetail,
            wickets: wickets
          })
        }

      })

    })

    if(batterDetails.length > 0) {
      // Sort batsmen in ascending order on runs
      batterDetails.sort(common.compareEntities('runs'))
      players['Batsman'] = {
        keys : [
          'Match', 'Runs'
        ],
        // reverse array so that batsman with most runs is shown first
        list : batterDetails.reverse()
      }
    }

    if(bowlerDetails.length > 0) {
      // Sort bowlers in ascending order on wickets
      bowlerDetails.sort(common.compareEntities('wickets'))
      players['Bowler'] = {
        keys : [
          'Match', 'Wkts'
        ],
        // reverse array so that bowler with most wicket is shown first
        list : bowlerDetails.reverse()
      }
    }

    //console.info('players: ', players)
    return players
  },

  getScoresStandings: function (standings = {}, competitors = []) {
    let teams = {}
    let awayhome = 'home'
    let standingData = {}
    //console.info('standings: ', standings)
    competitors.map((competitor) => {
      // Populate teams object with home and away data
      teams[competitor.id] = competitor.homeAway
    })
    //console.info('teams: ', teams)
    Object.keys(standings).map((stnd) => {
      awayhome = teams[stnd]
      if(typeof awayhome !== 'undefined') {
        standingData[awayhome] = standings[stnd]
      }
    })
    return standingData
  },

  /**
   * Get playerCardsData details for batter and bowler
   * @param  {Object} playerCardsData player Cards
   * @param  {Array}  competitors competitors array from feed
   * @return {Object} playerCardsData object with {home, away}
   */
  getPlayerData: function (playerCardsData={}, competitors=[]) {
    let players = {}
    let teams = {}
    let batter = selectn('batsmen',playerCardsData) || []
    let bowler = selectn('bowler',playerCardsData) || []

    const battingStatMap = {
      runs: { text: 'R', index: 0 },
      ballsFaced: { text: 'B', index: 1 },
      fours: { text: '4S', index: 2 },
      sixes: { text: '6S', index: 3 }
    }
    const bowlingStatMap = {
      overs: { text: 'O', index: 0 },
      maidens: { text: 'M', index: 1 },
      conceded: { text: 'R', index: 2 },
      wickets: { text: 'W', index: 3 }
    }

    competitors.map((competitor) => {
      // Populate teams object with team id and abbreviation data
      teams[competitor.id] = competitor.team.abbreviation || ''
    })

    // construct batter and bowler array for the players table
    let batterDetails = []
    batter.map((bt) => {
      const statsNode = selectn('stats', bt)
      // cache athlete name
      let athleteName = selectn('athlete.displayName', bt) || ''
      // check if this batsman is on strike
      let isStriker = bt.activeName === 'striker' ? true : false
      // Suffix * since batsman is on strike
      if (athleteName !== '' && isStriker === true) {
        athleteName = athleteName + '*'
      }
      batterDetails.push({
        name : athleteName,
        teamAbbr: teams[bt.teamId] || '',
        stats: common.filterStatsByNames(statsNode, battingStatMap),
        // use this flag for sorting
        onStrike : isStriker ? 1 : 0
      })
    })

    let bowlerDetails = []
    bowler.map((bw) => {
      const statsNode = selectn('stats', bw)
      // cache athlete name
      let athleteName = selectn('athlete.displayName', bw) || ''
      // check if this bowler is currently bowling
      let isStriker = bw.activeName === 'current bowler' ? true : false
      // Suffix * since bowler is currently bowling
      if (athleteName !== '' && isStriker === true) {
        athleteName = athleteName + '*'
      }
      bowlerDetails.push({
        name : athleteName,
        teamAbbr: teams[bw.teamId] || '',
        stats: common.filterStatsByNames(statsNode, bowlingStatMap),
        // use this flag for sorting
        onStrike : isStriker ? 1 : 0
      })
    })

    if(batter && batter.length > 0 && batterDetails.length > 0) {
      // Sorts on ascending order - non strike player will be first in array.
      // Reverse later
      batterDetails.sort(common.compareEntities('onStrike'))
      players['Batsman'] = {
        keys : [
          'R', 'B', '4s', '6s'
        ],
        list : batterDetails.reverse()
      }
    }

    if(bowler && bowler.length > 0 && bowlerDetails.length > 0) {
      // Sorts on ascending order - non strike player will be first in array.
      // Reverse later
      bowlerDetails.sort(common.compareEntities('onStrike'))
      players['Bowler'] = {
        keys : [
          'O', 'M', 'R', 'W'
        ],
        list : bowlerDetails.reverse()
      }
    }

    /*console.log('payercard: ', players)*/
    return players
  },

  getCurrentLeague: function (scores=[], prevSeriesId) {
    let startDate = ''
    let endDate = ''
    let calendar = []
    let currDate = moment.utc(new Date()).format()
    let currSeries = ''

    for (var i = 0; i < scores.length; i++) {
      let score = scores[i]
      calendar = selectn('leagues[0].calendar', score)
      if(calendar.length > 0) {
        startDate = calendar[0]
        startDate = moment(startDate).format()
        endDate = calendar[calendar.length - 1]
        endDate = moment(endDate).format()
        let iscurr = isCurrentLeague(startDate, endDate, currDate)
        //currSeries = selectn('leagues[0].id', score)
        //console.info('calendar:::::::::', currDate, startDate, endDate, iscurr, currSeries)
        //console.info('comparassion:::::', currDate > startDate, prevSeriesId)
        if(iscurr) {
          currSeries = selectn('leagues[0].id', score)
          break
        } else if(currDate > startDate) {
          currSeries = prevSeriesId
        } else if(currDate < startDate) {
          prevSeriesId = selectn('leagues[0].id', score)
        }
      }

    }
    //console.info('currSeries::::::::::', currSeries)
    return currSeries
  },

  /**
   * Get home and away teams details
   * @param  {Array}  headToHead  head to head
   * @param  {Array}  competitors competitors array from feed
   * @param  {Object}  eventclass  eventclass
   * @return {Object}             teams object with {home, away}
   */
  getHeadSummary: function (headToHead=[], competitors=[], eventclass={}) {
    let headDetails = {}
    let teams = {}
    let awayhome = 'home'
    let win = {}
    headDetails['format'] = eventclass.name
    competitors.map((competitor) => {
      // Populate teams object with home and away data
      teams[competitor.id] = competitor.homeAway
    })

    headToHead.map((headsummary) => {
      // default to empty object if not available
      const headCompetitor = headsummary.competitors || []
      headCompetitor.map((competitor) => {
        awayhome = teams[competitor.id]
        win[awayhome] = competitor.wins
      })
      headDetails['wins'] = win
      headDetails.tied = headsummary.ties
      headDetails.matches = headsummary.totalCompetitions
      headDetails.draw = headsummary.draws
    })
    //console.info("headDetails: ",headDetails)
    return headDetails

  },

  /**
   * Get score of a team
   * @param  {Object} competitor Each competitior from competitors array
   * @return {String}            Score if found else empty string
   */
  getTeamScores: function (competitor = {}) {
    return 'score' in competitor ? competitor.score : ''
  },

  /**
   * Filter links for teams. Used as a callback function.
   * @param  {Object} obj Link Object
   * @return {Boolean}     true if link text is found in valid array else false
   */
  filterLinks: function (obj) {
    // Lookup links with text
    let valid = [ 'Fixtures', 'Statistics', 'Squad', 'Clubhouse' ]
    let linkText = obj.text
    // Return false If valid string is not found in the link text
    if (valid.indexOf(linkText) === -1) {
      return false
    }
    // Get link type object
    let linkType = obj.rel
    // valid link type
    let validType = 'desktop'
    // Return true if desktop is found in rel array of the link object
    if (Array.isArray(linkType) === true) {
      if (linkType.indexOf(validType) > -1) {
        return true
      }
    }
    return false
  },

  /**
   * Get team links for 'Fixtures', 'Statistics', 'Squad'
   * @param  {Object} team team Object
   * @return {Array}       Array of team links
   */
  getTeamLinks: function (team) {
    let teamLinks = []
    //console.info('inside getTeamLinks', team)
    let links = team.links
    // filter out required links
    teamLinks = links.filter(scoreboard.filterLinks)
    //console.info('links, size', links, links.length)
    //console.info('teamLinks, size', teamLinks, teamLinks.length)
    let href = selectn('[0].href',teamLinks) || ''
    if (href !== '') {
      teamLinks[0].href = common.buildURL(href)
    }

    return teamLinks
  },

  getActiveDates: function (leagues) {
    let dates = {}
    let calendar = []

    if(typeof leagues !== 'undefined') {
      leagues.map(function (league) {
        calendar = calendar.concat(league.calendar)
      })

      calendar.map(function (date) {
        dates[(new Date(date).setHours(0,0,0,0))] = 1
      })

      return dates
    }
  },

  getDate: function (date, params = {}, query = {}) {
    let dateQuery = query.date && moment.parseZone(query.date, 'YYYYMMDD') || null
    let leagueQuery = params.league || query.league
    let eventDate = typeof date !== 'undefined' ? moment.parseZone(date, 'YYYYMMDD') : moment.utc()
    // default date is the date passed as this is the one got from the first match in the results
    let finalDate = params.sport !== 'cricket' ? eventDate : moment.utc()

    // if league query, use event date
    if(leagueQuery) {
      finalDate = eventDate
    }

    // dateQuery supercedes all dates
    if (dateQuery) {
      finalDate = dateQuery
    }

    return finalDate
  },

  shouldPoll(date) {
    const duration = 20000
    let enabled = false

    const startTime = moment.utc()
    const endTime = moment(date)
    const diff = endTime.diff(startTime, 'days')
    // console.log(startTime.format(), endTime.format(), diff)
    // TODO: update and optimize logic
    // polling 5 days, before and after the game, considering test games
    if(Math.abs(diff) <= 5) {
      enabled = true
    }
    return { enabled, duration }
  }

}

export default scoreboard



// WEBPACK FOOTER //
// ../app/lib/transform/parsers/cricket/scoreboard/index.js